'use client'

import { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { Button, Cell, Spacer, Text } from '@vinted/web-ui'
import { isNil, omitBy } from 'lodash'
import classNames from 'classnames'

import { useConversationContext } from '@marketplace-web/domain/core-conversation'
import { ServiceFeeIncludedTitle } from '@marketplace-web/domain/escrow-pricing'
import { useFetch } from '@marketplace-web/shared/api-client'
import { useBreakpoint } from '@marketplace-web/shared/breakpoints'
import { navigateToPage } from '@marketplace-web/shared/browser'
import { useTracking } from '@marketplace-web/shared/event-tracker'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { ProductItemModel } from '@marketplace-web/domain/product-item-data'
import {
  BundleShippingType,
  getBundleOrderReview,
  createBundleTransaction,
  editBundleTransaction,
  transformInstantBundleSummaryDto,
  BundleSummaryModel,
} from '@marketplace-web/domain/items-bundle-data'

import { buyerBuyEvent, buyerWantItemEvent, clickEvent } from '_libs/common/event-tracker/events'
import { formatCurrencyAmount } from '_libs/utils/formatString'
import { getHomepageSessionIdFromUrl } from '_libs/utils/homepage'

import { CHECKOUT_URL } from 'constants/routes'
import { CONVERSATION_URL } from 'constants/routes/inbox'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'

import { transformTransaction } from 'data/transformers/transaction'
import { useNavigateToSingleCheckout } from 'pages/SingleCheckout/hooks'
import { TransactionDto } from 'types/dtos/transaction'

import ItemThumbnails from '../ItemThumbnails'
import InstantBundleSummaryDialog from '../InstantBundleSummaryDialog'

type Props = {
  selectedItems: Array<ProductItemModel>
  transactionId: number | null
  bundleTrackingId: string | null
  bundleSummary: BundleSummaryModel | undefined
  isSellerBusiness: boolean
  homepageSessionId: string | null
  sellerId: number
}

type TransactionConfirmationProps = {
  bundleShippingType?: BundleShippingType | null
  redirectToCheckout?: boolean
  openOfferRequestModal?: boolean
}

const BundleFooter = ({
  bundleSummary,
  bundleTrackingId,
  isSellerBusiness,
  selectedItems,
  transactionId,
  homepageSessionId,
  sellerId,
}: Props) => {
  const { track } = useTracking()
  const translate = useTranslate('bundle_items')
  const breakpoints = useBreakpoint()
  const { locale } = useIntl()
  const { setTransaction } = useConversationContext()

  const { shouldRedirectToNew, navigateToNewCheckout, trackSingleCheckoutABExpose } =
    useNavigateToSingleCheckout()

  const [isModalVisible, setIsModalVisible] = useState(false)
  const itemThumbnailsContainerRef = useRef<HTMLDivElement | null>(null)

  const selectedItemIds = selectedItems.map(({ id }) => id)

  const { fetch: fetchOrderReview, transformedData: orderReview } = useFetch(
    getBundleOrderReview,
    response => transformInstantBundleSummaryDto(response.bundle_order_review),
  )

  useEffect(() => {
    if (!selectedItems.length) return

    const itemIds = selectedItems.map(({ id }) => id)

    fetchOrderReview({ itemIds, sellerId })
  }, [fetchOrderReview, selectedItems, sellerId])

  const handleTransactionResponseSuccess = (transaction: TransactionDto) => {
    const transactionResponse = transformTransaction(transaction)

    setTransaction(transactionResponse)
  }

  const createTransaction = async ({
    bundleShippingType,
    redirectToCheckout = false,
    openOfferRequestModal = false,
  }: TransactionConfirmationProps) => {
    track(
      clickEvent({
        screen: Screen.Bundling,
        target: ClickableElement.CreateBundle,
        targetDetails: JSON.stringify(
          omitBy(
            {
              bundle_id: bundleTrackingId,
              transaction_id: transactionId,
            },
            isNil,
          ),
        ),
      }),
    )

    selectedItemIds.forEach(id =>
      track(
        buyerWantItemEvent({
          transactionId,
          itemId: id,
          button: 'create_bundle',
          globalSearchSessionId: null,
          searchSessionId: null,
          homepageSessionId,
        }),
      ),
    )

    const response = await createBundleTransaction({
      selectedItemIds,
      bundleTrackingId,
      bundleShippingType,
    })

    if ('errors' in response) return

    const {
      transaction: { id, user_msg_thread_id },
    } = response

    if (redirectToCheckout) {
      track(
        buyerBuyEvent({
          transactionId: transactionId || id,
          screen: Screen.BundleSummary,
          homepageSessionId: getHomepageSessionIdFromUrl(),
        }),
      )

      trackSingleCheckoutABExpose()

      if (shouldRedirectToNew) {
        navigateToNewCheckout(id)
      } else {
        navigateToPage(CHECKOUT_URL(id))
      }

      return
    }

    if (openOfferRequestModal) {
      handleTransactionResponseSuccess(response.transaction)

      return
    }

    navigateToPage(CONVERSATION_URL(user_msg_thread_id))
  }

  const editTransaction = async ({
    bundleShippingType,
    redirectToCheckout = false,
    openOfferRequestModal = false,
  }: TransactionConfirmationProps) => {
    if (!transactionId) return

    const response = await editBundleTransaction({
      transactionId,
      selectedItemIds,
      bundleTrackingId,
      bundleShippingType,
    })

    if ('errors' in response) return

    const {
      transaction: { id, user_msg_thread_id },
    } = response

    if (redirectToCheckout) {
      track(
        buyerBuyEvent({
          transactionId: transactionId || id,
          screen: Screen.BundleSummary,
          homepageSessionId: getHomepageSessionIdFromUrl(),
        }),
      )

      if (shouldRedirectToNew) {
        navigateToNewCheckout(id)
      } else {
        navigateToPage(CHECKOUT_URL(id))
      }

      return
    }

    if (openOfferRequestModal) {
      handleTransactionResponseSuccess(response.transaction)

      return
    }

    navigateToPage(CONVERSATION_URL(user_msg_thread_id))
  }

  const handleToggleModal = () => setIsModalVisible(prevState => !prevState)

  const renderActions = () => {
    return (
      <Button
        text={translate('actions.review')}
        disabled={!selectedItems.length}
        size={Button.Size.Medium}
        styling={Button.Styling.Filled}
        inline
        onClick={handleToggleModal}
        testId="bundle-footer-action-button"
      />
    )
  }

  const renderPrices = () => {
    if (!bundleSummary) return null

    const { price, discountApplied, fullPrice, buyerProtectionFee, priceWithBuyerProtectionFee } =
      bundleSummary

    return (
      <div className="u-flexbox u-flex-direction-column u-flex-none">
        <div className="u-flexbox u-flex-direction-row">
          {discountApplied && (
            <>
              <Text
                as="h3"
                text={formatCurrencyAmount(price, locale)}
                type={Text.Type.Subtitle}
                theme="muted"
                testId="bundle-footer-discounted-price"
              />
              <Spacer orientation={Spacer.Orientation.Vertical} size={Spacer.Size.Small} />
            </>
          )}
          <Text
            as="h3"
            text={formatCurrencyAmount(fullPrice, locale)}
            type={Text.Type.Subtitle}
            theme="muted"
            strikethrough={discountApplied}
            testId="bundle-footer-full-price"
          />
        </div>
        <ServiceFeeIncludedTitle
          itemId={selectedItemIds[0]!}
          isSellerBusiness={isSellerBusiness}
          shouldTrimTitle
          shouldMinimiseTitle
          itemPrice={price}
          serviceFee={buyerProtectionFee}
          totalItemPrice={priceWithBuyerProtectionFee}
        />
      </div>
    )
  }

  const footerClassName = classNames('u-ui-padding-vertical-small', {
    'u-ui-padding-vertical-medium': breakpoints.tabletsUp,
  })

  return (
    <>
      <div className={footerClassName}>
        <Cell
          prefix={renderPrices()}
          suffix={renderActions()}
          body={
            <div className="u-tablets-up-only" ref={itemThumbnailsContainerRef}>
              <ItemThumbnails
                isDynamic
                items={selectedItems}
                containerRef={itemThumbnailsContainerRef}
              />
            </div>
          }
          styling={Cell.Styling.Tight}
        />
      </div>
      {orderReview && bundleSummary && (
        <InstantBundleSummaryDialog
          show={isModalVisible}
          onClose={handleToggleModal}
          bundleSummary={bundleSummary}
          instantBundleSummary={orderReview}
          bundleTrackingId={bundleTrackingId}
          selectedItems={selectedItems}
          onConfirm={transactionId ? editTransaction : createTransaction}
          sellerId={sellerId}
        />
      )}
    </>
  )
}

export default BundleFooter
